import { List } from 'immutable';
import get from 'lodash/get';
import keys from 'lodash/keys';
import compareAsc from 'date-fns/compareAsc';
import compareDesc from 'date-fns/compareDesc';
import startOfDay from 'date-fns/startOfDay';

import { caseInsensitiveIncludes, makeColumnSort, sortImmutableList } from '../../util';

import { SURVEYS__VERSION_PREVIEW } from '../constants';
import { selectCurrentUserId } from '../user/selectors.user';
import { isOrganizationAdmin } from '../memberships/selectors.memberships';
import { selectOrganization } from '../organizations/selectors.organizations';
import { selectSurveyListView } from '../view.survey-list/selectors.view.survey-list';
import { selectCurrencyBuId } from '../currencies/selectors.currencies';

const arrayMatch = (array, value) => {
  if (array.length === 0) return true;
  return array.indexOf(value) !== -1;
};
const dateMatch = (queryDate, date, reverse) => {
  if (!queryDate) return true;
  if (!date) return false;
  const compare = reverse ? compareDesc : compareAsc;
  return compare(startOfDay(date), queryDate) >= 0;
};
const intervalMatch = (query, fieldName, date) => {
  const dateFrom = query[`${fieldName}From`];
  const dateTo = query[`${fieldName}To`];
  return dateMatch(dateFrom, date) && dateMatch(dateTo, date, true);
};
const makeSurveysFilter =
  (organizationId, { createdBy, criteria, status, ...query }) =>
  (survey) => {
    if (organizationId !== survey.get('organizationId')) return false;
    const criteriaMatch = criteria ? caseInsensitiveIncludes(survey.get('title'), criteria) : true;
    const statusMatch = arrayMatch(status, survey.get('status'));
    const userMatch = arrayMatch(createdBy, survey.getIn(['createdBy', 'id']));
    const publishedAtMatch = intervalMatch(query, 'publishedAt', survey.get('publishedAt'));
    return criteriaMatch && statusMatch && userMatch && publishedAtMatch;
  };

export const selectAllSurveys = (state) =>
  state.surveys
    .toList()
    .map((survey) =>
      survey.set('organization', selectOrganization(state, survey.get('organizationId'))),
    );

export const selectSurveysRecent = (state, organizationId) =>
  selectAllSurveys(state)
    .filter((survey) => !organizationId || survey.get('organizationId') === organizationId)
    .sort((a, b) => b.get('publishedAt') - a.get('publishedAt'));

export const selectSurveysByOrganization = (state, organizationId, query) =>
  selectAllSurveys(state)
    .filter(makeSurveysFilter(organizationId, query))
    .sort(makeColumnSort(query, { sortBy: 'createdAt', sortDirection: 'desc' }));

export const selectSurveyById = (state, surveyId) => {
  const survey = state.surveys.get(surveyId);
  if (!survey) return survey;
  const currencyId = survey.getIn(['currency', 'id']);
  const currency = selectCurrencyBuId(state, currencyId);
  return survey && survey.merge({ currency }).toJS();
};

export const selectSurveyByToken = (state, token) => {
  if (!token) return undefined;
  const survey = state.surveys.find((v) => v.get('accessToken') === token);
  return survey ? survey.toJS() : undefined;
};

export const selectSurveyIsInactive = (state, token) => {
  const survey = state.surveys.find((v) => v.get('accessToken') === token);
  const result = survey ? survey.get('status') !== 'active' : false;
  return result;
};

export const selectSurveyFraudDetectionSettings = (state, surveyId) =>
  sortImmutableList(state.surveys.getIn([surveyId, 'fraudDetectionSettings'], new List()), {
    sortBy: 'label',
  });

export const surveyIsActive = (survey) => survey && survey.status === 'active';
export const getThemeClass = (survey) => `theme-${get(survey, 'theme')}`;

export const selectSurveyDisplayVersion = (version) =>
  version === 0 ? SURVEYS__VERSION_PREVIEW : version;

export const selectSurveyHash = ({ surveys }, surveyId) => surveys.getIn([surveyId, 'dirtyHash']);

export const selectSurveyVariables = (survey) => {
  if (!survey) return [];
  const { variables } = survey;
  return keys(variables)
    .map((name) => ({
      ...variables[name],
      name,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
};

export const selectSurveyVariable = (variables, variableName) =>
  variables.filter(({ name }) => name === variableName).shift();

const makeDeletablePredicate = (state) => (surveyId) => {
  const survey = state.surveys.get(surveyId);
  if (!survey) return false;
  const organizationId = survey.get('organizationId');
  if (isOrganizationAdmin(state, organizationId)) return true;
  const currentUserId = selectCurrentUserId(state);
  return survey.getIn(['createdBy', 'id']) === currentUserId;
};
export const selectDeletableSurveys = (state, query) =>
  selectSurveyListView(state, query).get('checked').filter(makeDeletablePredicate(state));
